import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A marker element that re-renders its parent Shopify section when the
 * selected variant changes.
 *
 * The variant picker fetches the full product page for the new variant and
 * dispatches a `variant:update` event carrying the parsed document. This
 * component finds its own section in that document and morphs the live
 * section from it, so any variant-level metafield read by the section's
 * Liquid stays in sync with the selected variant.
 *
 * Render it unconditionally inside the section — even when the section has
 * nothing else to output — so an empty → non-empty transition still has an
 * element left in the DOM to react to the next variant change.
 *
 * @example
 * <variant-section-refresh data-section-id="{{ section.id }}" hidden></variant-section-refresh>
 */
class VariantSectionRefresh extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * Morphs the surrounding Shopify section from the updated document.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const wrapperId = `shopify-section-${sectionId}`;
    const currentSection = this.closest(`#${CSS.escape(wrapperId)}`);
    const updatedSection = event.detail.data.html?.getElementById(wrapperId);

    // A quick-add or product-card update only renders that section, not the
    // full page — in that case the section is absent and there is nothing to do.
    if (!currentSection || !updatedSection) return;

    morph(currentSection, updatedSection, { childrenOnly: true });
    this.#reloadStaleVideos(currentSection);
    this.#replayBlockAnimations(currentSection);
  };

  /**
   * The server renders animated blocks in their pre-animation state (no
   * `is-visible` class → opacity 0) and block-animations.js never re-observes
   * morphed elements, so they would stay invisible after the morph. Re-adding
   * the class replays the entrance animation on the refreshed content.
   * @param {Element} section - The morphed section.
   */
  #replayBlockAnimations(section) {
    for (const block of section.querySelectorAll('[data-block-animate]:not(.is-visible)')) {
      block.classList.add('is-visible');
    }
  }

  /**
   * A playing <video> keeps its current stream when the morph swaps the src of
   * its <source> child, and re-loading it in place can leave a blank compositor
   * layer — replacing the element with a fresh clone restarts resource
   * selection, autoplay, and painting from scratch.
   * @param {Element} section - The morphed section.
   */
  #reloadStaleVideos(section) {
    for (const video of section.querySelectorAll('video')) {
      const target = video.querySelector('source')?.src || video.src;
      if (!target || !video.currentSrc || video.currentSrc === target) continue;

      const fresh = /** @type {HTMLVideoElement} */ (video.cloneNode(true));
      // Chrome does not carry the muted attribute over to a clone's muted state; left unmuted,
      // the clone's autoplay is refused by its autoplay policy.
      if (fresh.hasAttribute('muted')) fresh.muted = true;
      video.replaceWith(fresh);
    }
  }
}

if (!customElements.get('variant-section-refresh')) {
  customElements.define('variant-section-refresh', VariantSectionRefresh);
}
